import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../lib/firebase";
/* import { format } from "date-fns"; */
import { ArrowLeft, Package, Truck, CheckCircle, Mail, ExternalLink } from "lucide-react"; 

const FulfillOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // New State for Tracking
  const [trackingNumber, setTrackingNumber] = useState("");

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const docRef = doc(db, "orders", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setOrder({ id: docSnap.id, ...data });
          // Pre-fill tracking number if it already exists in DB
          if (data.trackingNumber) setTrackingNumber(data.trackingNumber);
        } else {
          console.error("No such order!");
        }
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const updateStatus = async (newStatus) => {
    if (newStatus === 'shipped' && !trackingNumber.trim()) {
      alert("Please enter a tracking number before marking as shipped.");
      return;
    }

    setUpdating(true);
    try {
      const orderRef = doc(db, "orders", id);
      const updates = { status: newStatus };
      
      // Save tracking number to Firestore when shipping
      if (newStatus === 'shipped') {
        updates.trackingNumber = trackingNumber;
      }

      await updateDoc(orderRef, updates);
      setOrder((prev) => ({ ...prev, ...updates }));
      alert(`Order successfully marked as ${newStatus}`);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update order.");
    } finally {
      setUpdating(false);
    }
  };

  const openTracking = () => {
    const url = `https://www.purolator.com/en/shipping/tracker?pin=${order.trackingNumber}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading order details...</div>;
  if (!order) return <div className="p-10 text-center text-red-500">Order not found.</div>;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 hover:text-purple-700 mb-6 transition"
      >
        <ArrowLeft size={20} className="mr-2" /> Back to Orders
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary Card */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Order Details</h1>
                <p className="text-sm text-gray-500">ID: {order.id}</p>
              </div>
              <span className={`px-4 py-1 rounded-full text-sm font-bold uppercase ${
                order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                order.status === 'shipped' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
              }`}>
                {order.status}
              </span>
            </div>

            <div className="space-y-4">
              {order.products.map((item, index) => (
                <div key={index} className="flex items-center justify-between border-b pb-4 last:border-0">
                  <div className="flex items-center gap-4">
                    <img src={item.imageUrl} alt={item.name} className="w-16 h-16 object-cover rounded-lg border" />
                    <div>
                      <p className="font-semibold text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                  </div>
                  <p className="font-bold text-gray-800">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* NEW: Tracking & Fulfillment Actions */}
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Fulfillment Actions</h3>
            
            {/* Tracking Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purolator Tracking Number (PIN)
              </label>
              <div className="flex gap-2">
                <input 
                  type="text"
                  placeholder="Enter PIN (e.g. 331234567890)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  disabled={order.status === 'delivered'}
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none disabled:bg-gray-50"
                />
                {order.trackingNumber && (
                  <button 
                    onClick={openTracking}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center shadow-sm transition"
                  >
                    <ExternalLink size={16} className="mr-2" /> Track
                  </button>
                )}
              </div>
              {!order.trackingNumber && (
                <p className="text-xs text-gray-500 mt-2 italic">
                  * Tracking number is required to mark as Shipped.
                </p>
              )}
            </div>

            <hr />

            {/* Status Buttons */}
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => updateStatus('pending')}
                disabled={updating || order.status === 'pending'}
                className="flex items-center px-4 py-2 bg-white border border-yellow-400 text-yellow-700 rounded-lg hover:bg-yellow-50 disabled:opacity-50 transition"
              >
                <Package size={18} className="mr-2" /> Pending
              </button>

              <button 
                onClick={() => updateStatus('shipped')}
                disabled={updating || order.status === 'shipped' || !trackingNumber.trim()}
                className={`flex items-center px-4 py-2 rounded-lg transition font-medium ${
                  !trackingNumber.trim() 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                }`}
              >
                <Truck size={18} className="mr-2" /> Mark Shipped
              </button>

              <button 
                onClick={() => updateStatus('delivered')}
                disabled={updating || order.status === 'delivered' || order.status === 'pending'}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 shadow-md transition"
              >
                <CheckCircle size={18} className="mr-2" /> Mark Delivered
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Customer Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Customer Info</h3>
            <div className="space-y-3 text-sm">
              <p><strong className="text-gray-500">Name:</strong> {order.shipping?.name}</p>
              <p className="flex items-center">
                <strong className="text-gray-500 mr-2">Email:</strong> 
                <a href={`mailto:${order.shipping?.email}`} className="text-purple-600 hover:underline flex items-center">
                  {order.shipping?.email} <Mail size={14} className="ml-1"/>
                </a>
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Shipping Address</h3>
            <div className="text-sm text-gray-600 leading-relaxed">
              <p className="font-semibold text-gray-800">{order.shipping?.name}</p>
              <p>{order.shipping?.address?.line1}</p>
              <p>{order.shipping?.address?.city}, {order.shipping?.address?.state} {order.shipping?.address?.postal_code}</p>
              <p>{order.shipping?.address?.country}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FulfillOrder;